import compact from "lodash/compact";
import uniq from "lodash/uniq";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { VoiceSettingsReasonItem } from "@/services/model";
import { NamespaceKey } from "@/utils/translationKey";

const REASONS_PREFIX = "cockpit.managementTile.voiceSettings.reasons";

/** Full `portal` keys: the unpaid-bill sentence is shared with add-device. */
const reasonKey: Partial<
  Record<VoiceSettingsReasonItem, NamespaceKey<"portal">>
> = {
  [VoiceSettingsReasonItem.BillNotPaid]: "common.openBillReason",
  // Dunning starts from an unpaid bill, so it reads the same to the customer.
  [VoiceSettingsReasonItem.BillDunning]: "common.openBillReason",
  [VoiceSettingsReasonItem.NoActiveVoicePackage]: `${REASONS_PREFIX}.noActiveVoicePackage`,
  [VoiceSettingsReasonItem.NoPaymentMean]: `${REASONS_PREFIX}.noPaymentMean`,
  [VoiceSettingsReasonItem.CreditClassC]: `${REASONS_PREFIX}.creditClassC`,
  [VoiceSettingsReasonItem.CostLimitReached]: `${REASONS_PREFIX}.costLimitReached`,
};

/**
 * Why the call settings cannot be changed, ready to drop into the tab's
 * "Good to know" panel. Returns undefined while they are editable.
 */
export const useBlockedVoiceSettingsReason = ({
  blocked,
  isServicePending,
  reasons,
}: {
  blocked: boolean;
  isServicePending: boolean;
  reasons?: VoiceSettingsReasonItem[];
}): ReactNode | undefined => {
  const { t } = useTranslation(undefined, { keyPrefix: REASONS_PREFIX });
  const { t: tPortal } = useTranslation("portal");

  if (!blocked) {
    return undefined;
  }

  if (isServicePending) {
    return t("servicePendingBlocked");
  }

  // Unpaid bills and dunning share a sentence, so the API returning both must
  // not list it twice.
  const translated = uniq(
    compact(
      (reasons ?? []).map((reason) =>
        reasonKey[reason] ? tPortal(reasonKey[reason]) : undefined,
      ),
    ),
  );

  // Blocked without a reason the map knows: still say that it is blocked and
  // where to ask, rather than leaving locked switches unexplained.
  if (translated.length === 0) {
    return t("askSupport");
  }

  return (
    <>
      {t("voiceSettingsBlocked")}
      <ul className="list-inside list-disc">
        {translated.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
      {t("askSupport")}
    </>
  );
};
